import type { BridgeMessage } from "@visorxr/core";
import { SessionRecorder } from "./session-recorder";
import type { DeviceInfo, Envelope } from "./session-recorder";
import { TelemetryTransport } from "./transport";
import type { Sender } from "./transport";
import { createBeaconSender } from "./beacon-sender";
import type { TrackingObservation, TrackingObserver } from "../backends/tracking-observation";

export interface Telemetry {
  readonly observe: TrackingObserver;
  setEnvelope(patch: Partial<Envelope>): void;
  started(backend: Envelope["backend"]): void;
  recordBridge(message: BridgeMessage): void;
  /** The platform refused audible playback and the unmute affordance was offered. */
  audioBlocked(): void;
  /** The user took the offer. Blocked-without-unmuted is the sessions the affordance failed. */
  unmuted(): void;
  /** The named poster played audio without one decoded video frame, and its pipeline was rebuilt. */
  videoStalled(target: number): void;
  /** Frames arrived after the rebuild — stalled-without-recovered is the heal failing. */
  videoRecovered(target: number): void;
  flush(final?: boolean): void;
}

export interface TelemetryDeps {
  build: string;
  sender?: Sender;
  now?: () => number;
  page?: EventTarget;
  /** A factory, not a promise: with telemetry off nothing should be asking the platform about the
   * device at all. */
  device?: () => Promise<DeviceInfo>;
  sessionId?: string;
  startedAt?: number;
}

/**
 * Two unrelated origins, and both are load-bearing. One cannot distinguish a path from a URL that
 * happens to name the very origin it is being checked against: "//host//elsewhere/t" resolved
 * against `https://host` keeps that origin and reports "same origin", while the path it yields is
 * protocol-relative and lands on `elsewhere` once the browser resolves it against the real page. A
 * value that stays put against two different origins cannot be naming either of them.
 */
const FIRST_PROBE_ORIGIN = "https://first.telemetry-origin-probe.invalid";
const SECOND_PROBE_ORIGIN = "https://second.telemetry-origin-probe.invalid";
/** Exported so a test can aim the one attack that only these hosts can mount. */
export const PROBE_ORIGINS = [FIRST_PROBE_ORIGIN, SECOND_PROBE_ORIGIN] as const;

/**
 * Telemetry runs only against a same-origin path. An absolute URL would mean CORS, a preflight on
 * every beacon, and a third party holding session data — none of which this feature is willing to
 * have, so the refusal lives here rather than in a deployment note.
 *
 * Decided by resolving rather than by string tests, because the string a browser beacons to is not
 * the string configured here: URL parsing strips tab, newline and carriage return, reads a backslash
 * as a separator, and removes dot segments. Only the parser knows every such rewrite, so the check
 * asks it instead of imitating it.
 *
 * The configured value must therefore already *be* the path that will be requested, and is returned
 * untouched. Handing back a normalized form instead is what let "/.//collector.example/t" — a plain
 * same-origin path when checked — come back as "//collector.example/t" and resolve off-host when
 * sent. There is no derived value here for the parser to disagree with.
 */
export function resolveTelemetryUrl(raw: string | undefined): string | null {
  // An absolute path, not merely a relative one: a bare "telemetry" would resolve against whatever
  // directory the player happens to be served from, which is same-origin but not what was meant.
  if (!raw?.startsWith("/")) return null;
  try {
    const onFirst = new URL(raw, FIRST_PROBE_ORIGIN);
    const onSecond = new URL(raw, SECOND_PROBE_ORIGIN);
    // Origin-preserving against two unrelated origins, so origin-preserving against the page's.
    if (onFirst.origin !== FIRST_PROBE_ORIGIN || onSecond.origin !== SECOND_PROBE_ORIGIN) return null;
    // …and unchanged by resolution, so the path checked here is the path the network sees.
    return `${onFirst.pathname}${onFirst.search}` === raw ? raw : null;
  } catch {
    return null;
  }
}

/**
 * `crypto.randomUUID` exists only in a secure context (and only from Safari 15.4), and an insecure
 * origin is exactly the session worth hearing about — the player cannot start there, and this must
 * not be the reason why. The fallback is UUID-shaped because the receiver accepts nothing else.
 */
function newSessionId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (digit) =>
    (Number(digit) ^ ((Math.random() * 16) >> (Number(digit) / 4))).toString(16),
  );
}

/** Random per page load and never persisted: two sessions from one device cannot be linked. */
function buildEnvelope(deps: TelemetryDeps): Envelope {
  return {
    v: 1,
    sessionId: deps.sessionId ?? newSessionId(),
    startedAt: deps.startedAt ?? Date.now(),
    build: deps.build,
    campaignId: null,
    exp: null,
    backend: null,
    supported: [],
    motion: null,
    markers: null,
    device: null,
    camera: null,
    videoQualityApi: null,
    videoTexturePath: null,
    bins: {},
  };
}

/** Guarded so a telemetry bug costs a session's data and never the experience it was measuring. */
type Guard = (work: () => void) => void;

function bindLifecycle(page: EventTarget, guard: Guard, recorder: SessionRecorder, transport: TelemetryTransport): void {
  page.addEventListener("visibilitychange", () => {
    // Read from the page that raised the event, not the global one: the seam and the check would
    // otherwise disagree, and the branch nothing can drive is the branch nothing tests.
    // Hidden is the last moment iOS reliably gives a page; visible is only worth its event because
    // it explains the gap in frames that follows.
    const hidden = (page as Partial<Document>).visibilityState !== "visible";
    guard(() => recorder.record({ kind: hidden ? "hidden" : "visible" }, performance.now()));
    if (hidden) guard(() => transport.flush());
  });
  page.addEventListener("pagehide", () => guard(() => transport.flush(true)));
}

export function createTelemetry(rawUrl: string | undefined, deps: TelemetryDeps): Telemetry | null {
  const url = resolveTelemetryUrl(rawUrl);
  if (url === null) return null;

  const recorder = new SessionRecorder(buildEnvelope(deps));
  const transport = new TelemetryTransport({
    url,
    recorder,
    sender: deps.sender ?? createBeaconSender(typeof navigator === "undefined" ? {} : navigator, globalThis.fetch),
    ...(deps.now ? { now: deps.now } : {}),
  });
  void deps
    .device?.()
    .then((device) => recorder.setEnvelope({ device }))
    .catch(() => undefined);

  let live = true;
  const guard: Guard = (work) => {
    if (!live) return;
    try {
      work();
    } catch (error) {
      live = false;
      transport.stop();
      console.warn("visorxr: telemetry disabled after an internal error", error);
    }
  };

  // Bound at `started`, not here: a page that is opened and bounced would otherwise flush on its
  // own pagehide and land as a session with no backend and nothing in it, on every page view. A
  // failure before start still reports itself — that path flushes explicitly.
  const page = deps.page ?? (typeof document === "undefined" ? undefined : document);
  let lifecycleBound = false;

  return {
    observe: (observation: TrackingObservation) =>
      guard(() => {
        recorder.observe(observation);
        // The frame path is the one that bursts; without this the pending-event valve never opens
        // and a single chunk can carry a whole session's transitions.
        transport.noteEvent();
      }),
    setEnvelope: (patch) => guard(() => recorder.setEnvelope(patch)),
    started: (backend) =>
      guard(() => {
        if (page && !lifecycleBound) {
          bindLifecycle(page, guard, recorder, transport);
          lifecycleBound = true;
        }
        recorder.setEnvelope({ backend });
        recorder.record({ kind: "started", backend }, performance.now());
        // The heartbeat begins here, not at construction: a page that is opened and never started
        // has nothing to report, and a file per page view would drown the sessions that do.
        transport.start();
        transport.flush();
      }),
    recordBridge: (message) =>
      guard(() => {
        // Only the fact and its code: an error message can carry a path or a URL, and the photo is
        // the one thing this feature exists to never transmit.
        if (message.type === "error") {
          recorder.record({ kind: "error", code: message.code }, performance.now());
          // Sent immediately: a session that fails to start is often a session that is about to be
          // closed, and waiting out the heartbeat loses exactly the report worth having.
          transport.flush();
          return;
        }
        if (message.type === "photo-captured") recorder.record({ kind: "captured" }, performance.now());
        transport.noteEvent();
      }),
    audioBlocked: () =>
      guard(() => {
        recorder.record({ kind: "audio-blocked" }, performance.now());
        transport.noteEvent();
      }),
    unmuted: () =>
      guard(() => {
        recorder.record({ kind: "unmuted" }, performance.now());
        transport.noteEvent();
      }),
    videoStalled: (target) =>
      guard(() => {
        recorder.record({ kind: "video-stalled", target }, performance.now());
        transport.noteEvent();
      }),
    videoRecovered: (target) =>
      guard(() => {
        recorder.record({ kind: "video-recovered", target }, performance.now());
        transport.noteEvent();
      }),
    flush: (final) => guard(() => transport.flush(final)),
  };
}
